import {
    ExcelExportParams,
    ProcessCellForExportParams,
    RowClassParams,
} from 'ag-grid-community';
import { t } from 'i18next';
import {
    ORG_CHART_HR_MAPPING,
    ORG_CHART_HR_SOFTWARE_ID,
} from 'routes/HumanResourcesModule/business/definitions';
import {
    agentValueFormatter,
    AGENT_FIELD,
} from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/agentColumnHelpers';
import { orgChartHrValueFormatter } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import {
    AgentArrivalHypothesisType,
    AGENT_ARRIVAL_HYPOTHESIS,
} from '../components/Arrival/helpers/constants';
import { AgentArrivalHypothesis } from '../components/Arrival/helpers/types';
import {
    AGENT_DEPARTURE_HYPOTHESIS,
    AgentDepartureHypothesisType,
    AGENT_DEPARTURE_HYPOTHESIS_TYPES_BY_RECURRENCE,
} from '../components/Departure/helpers/constants';
import {
    DetailedAgentDepartureHypothesis,
    AgentDepartureHypothesis,
} from '../components/Departure/helpers/types';
import { UNMAPPED } from 'business/constants';
import { ObsoleteServicesBySimulation } from 'routes/HumanResourcesModule/Simulation/helpers/hooks/simulation';
import { alpha } from '@mui/material/styles';
import {
    AGENT_IFSE_HYPOTHESES,
    AGENT_PONCTUAL_PRIME_HYPOTHESES,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/PayComponent/helpers/constants';

/**
 * Determines the display type for an agent's hypothesis based on its recurrence.
 *
 * Sometimes, there are recurrent and non-recurrent data for the same agent and the same
 * job number. The hypothesis then shows in both cases, but if the hypothesis type does not
 * make sense for a type of recurrence, we display a default type of "simple_departure".
 *
 * @param recurrence - The recurrence type of the agent
 * @param agentDepartureHypothesisType - The initial hypothesis type of the agent
 * @returns - The hypothesis type to be displayed
 */
export const getAgentDepartureHypothesisDisplayType = (
    recurrence: RecurrenceEnum,
    agentDepartureHypothesisType: AgentDepartureHypothesisType,
): AgentDepartureHypothesisType =>
    !recurrence ||
    AGENT_DEPARTURE_HYPOTHESIS_TYPES_BY_RECURRENCE[recurrence]?.includes(
        agentDepartureHypothesisType,
    )
        ? agentDepartureHypothesisType
        : AgentDepartureHypothesisType.SIMPLE_DEPARTURE;

export const getAgentArrivalHypothesisDisplayType =
    (): AgentArrivalHypothesisType => AgentArrivalHypothesisType.SIMPLE_ARRIVAL;

export const agentTypeValueFormatter = (
    agentDepartureHypothesisType: AgentDepartureHypothesisType,
) =>
    t(
        `humanResources.simulation.agentDepartureHypothesisType.${agentDepartureHypothesisType}`,
    );

function isValueIncluded(value: string, ObsoleteOrgChartHr: (string | null)[]) {
    for (const list of Object.values(ObsoleteOrgChartHr)) {
        if (list?.includes(value)) {
            return true;
        }
    }
    return false;
}

export const getObsoleteGridProps = ({
    hrSimulation,
    obsoleteOrgChartHr,
}: {
    hrSimulation: HrSimulation;
    obsoleteOrgChartHr: ObsoleteServicesBySimulation;
}) => {
    const obsoleteOrgChartHrArray =
        Object.keys(obsoleteOrgChartHr).length > 0
            ? obsoleteOrgChartHr[hrSimulation.id]
            : [];
    return {
        rowStyle: { color: alpha('#000', 0.87) },
        getRowStyle: (params: RowClassParams) => {
            if (
                params?.node.key?.includes(UNMAPPED) ||
                params.node.parent?.key?.includes(UNMAPPED) ||
                params?.data?.[ORG_CHART_HR_SOFTWARE_ID] === UNMAPPED ||
                (obsoleteOrgChartHr &&
                    isValueIncluded(
                        params?.node.key?.slice(-5) ?? '',
                        obsoleteOrgChartHrArray ?? [],
                    ))
            ) {
                return { color: 'grey' };
            }
            return { color: alpha('#000', 0.87) };
        },
    };
};
export const getDefaultAgentGridExportParams = (
    recurrence: RecurrenceEnum,
): ExcelExportParams => ({
    processCellCallback: (
        params: ProcessCellForExportParams<
            | AgentDepartureHypothesis
            | DetailedAgentDepartureHypothesis
            | AgentArrivalHypothesis
        >,
    ) => {
        const { field } = params.column.getColDef();
        if (field === AGENT_FIELD) return agentValueFormatter(params);
        if (field === ORG_CHART_HR_MAPPING) {
            return orgChartHrValueFormatter(params);
        }
        if (
            field === AGENT_DEPARTURE_HYPOTHESIS ||
            field === AGENT_ARRIVAL_HYPOTHESIS ||
            field === AGENT_IFSE_HYPOTHESES ||
            field === AGENT_PONCTUAL_PRIME_HYPOTHESES
        ) {
            const value: string[] = [];
            if (params.value === undefined) return '';
            if (params.value[AGENT_DEPARTURE_HYPOTHESIS]) {
                const agentDepartureHypothesisType =
                    getAgentDepartureHypothesisDisplayType(
                        recurrence,
                        params.value[AGENT_DEPARTURE_HYPOTHESIS]
                            .agentDepartureHypothesisType,
                    );
                value.push(
                    agentTypeValueFormatter(agentDepartureHypothesisType),
                );
            }
            if (params.value[AGENT_ARRIVAL_HYPOTHESIS]) {
                const arrivalHypothesisType =
                    getAgentArrivalHypothesisDisplayType();
                value.push(
                    t(
                        `humanResources.simulation.agentArrivalHypothesisType.${arrivalHypothesisType}`,
                    ),
                );
            }
            if (params.value[AGENT_IFSE_HYPOTHESES]) {
                value.push(
                    t(
                        'humanResources.simulation.agentHypothesis.ifseHypotheses',
                    ),
                );
            }
            if (params.value[AGENT_PONCTUAL_PRIME_HYPOTHESES]) {
                value.push(
                    t(
                        'humanResources.simulation.agentPayComponentHypothesisType.simple_prime',
                    ),
                );
            }
            return value.join(', ');
        }
        return params.value;
    },
});
